if (!customElements.get("predictive-search-v2")) {
  class PredictiveSearchV2 extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.drawer =
        this.closest("#Search-Drawer") ||
        document.getElementById("Search-Drawer");

      const scope = this.drawer || document;
      this.inputs = scope.querySelectorAll("[data-search-form-input]");
      this.forms = scope.querySelectorAll("[data-search-form]");
      this.wrapper = this.querySelector(".quick-search-wrapper");
      this.closeBtns = this.drawer
        ? this.drawer.querySelectorAll("side-panel-close, .search-form__close")
        : document.querySelectorAll(".search-form__close");
      this.toggleButtons = document.querySelectorAll(".thb-quick-search");

      this.lastSearchEl = this.querySelector("a[data-last-search]");
      this.predictiveSearchResults = this.querySelector(
        "[data-predictive-search-results]"
      );

      this.isLoading = false;
      this.initialResultsHTML = this.predictiveSearchResults
        ? this.predictiveSearchResults.innerHTML
        : null;

      this.setupEventListeners();
      this.buildLastSearch();
    }

    setupEventListeners() {
      const debounceFn =
        typeof debounce === "function"
          ? debounce
          : (fn, wait) => {
              let t;
              return (...a) => {
                clearTimeout(t);
                t = setTimeout(() => fn(...a), wait);
              };
            };

      this.inputs.forEach((input) => {
        input.addEventListener("input", () => {
          if (input.value.trim().length) this.isLoading = true;
        });

        input.addEventListener(
          "input",
          debounceFn(() => this.onChange(), 300).bind(this)
        );

        input.addEventListener("keydown", (event) => {
          if (event.key !== "Enter") return;
          if (!input.value.trim().length || this.isLoading) {
            event.preventDefault();
            return;
          }
          const firstLink = this.querySelector(
            ".quick-search-secondary-container a[href]:not(.quick-search-secondary-container-show-all)"
          );
          if (firstLink) {
            event.preventDefault();
            window.location.href = firstLink.getAttribute("href");
          }
        });
      });

      this.forms.forEach((form) => {
        form.addEventListener("submit", (event) => {
          if (!this.getQuery().length) {
            event.preventDefault();
            return;
          }
          this.saveCurrentSearch();
        });
      });

      this.closeBtns.forEach((btn) => {
        btn.addEventListener("click", () => this.close());
      });

      if (this.wrapper) {
        this.wrapper.addEventListener("click", (event) => {
          if (event.target === this.wrapper) this.close();
        });
      }

      this.toggleButtons.forEach((btn) => {
        btn.addEventListener("click", (event) => {
          event.preventDefault();
          this.toggle();
        });
      });
    }

    getActiveInput() {
      const activeEl = document.activeElement;
      return (
        Array.from(this.inputs).find((input) => activeEl === input) ||
        this.inputs[0]
      );
    }

    getQuery() {
      const input = this.getActiveInput();
      return (input && input.value.trim()) || "";
    }

    saveCurrentSearch() {
      const q = this.getQuery();
      if (q) {
        try {
          localStorage.setItem("predictiveSearchLastQuery", q);
        } catch (e) {
          /* ignore */
        }
      }
    }

    getLastSearch() {
      try {
        return localStorage.getItem("predictiveSearchLastQuery") || null;
      } catch (e) {
        return null;
      }
    }

    buildLastSearch() {
      this.lastSearchEl = this.querySelector("a[data-last-search]");
      if (this.lastSearchEl) {
        const lastSearch = this.getLastSearch();
        if (lastSearch) {
          this.lastSearchEl.innerHTML =
            lastSearch + this.lastSearchEl.innerHTML;
          this.lastSearchEl.setAttribute("data-search-query", lastSearch);
          this.lastSearchEl.classList.remove("hide");
          this.lastSearchEl.addEventListener("click", () => {
            this.getSearchResults(lastSearch);
          });
        } else {
          this.lastSearchEl.classList.add("hide");
        }
      }
      this.updateSecondaryVisibility();
    }

    updateSecondaryVisibility() {
      const containers = this.querySelectorAll(
        ".quick-search-secondary-container"
      );
      containers.forEach((container) => {
        const visibleLinks = container.querySelectorAll("a:not(.hide)");
        container.style.display = visibleLinks.length ? "" : "none";
      });
    }

    onChange() {
      const term = this.getQuery();
      if (!term.length) {
        this.restoreInitial();
        return;
      }
      this.getSearchResults(term);
      this.saveCurrentSearch();
    }

    buildSearchEndpoint(q) {
      const root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
        "/";
      return `${root}search/suggest?q=${encodeURIComponent(
        q.trim()
      )}&resources[type]=product,collection,article&limit=8&section_id=predictive-search-v2`;
    }

    getSearchResults(term) {
      const url = this.buildSearchEndpoint(term);
      this.isLoading = true;
      this.classList.add("loading");

      fetch(url)
        .then((response) => {
          if (!response.ok) {
            this.close();
            throw new Error(response.status);
          }
          return response.text();
        })
        .then((text) => {
          const fresh = new DOMParser()
            .parseFromString(text, "text/html")
            .querySelector("[data-predictive-search-results]");
          const html = fresh ? fresh.innerHTML : "";
          this.renderSearchResults(html, term);
          this.buildLastSearch();
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          this.isLoading = false;
          this.classList.remove("loading");
        });
    }

    renderSearchResults(html, term) {
      if (!this.predictiveSearchResults) return;
      this.predictiveSearchResults.innerHTML = html;
      this.open();
      this.notifyEmptyResults(term);
    }

    // Tells <search-fallback> that Shopify came up empty for this term, so it
    // can try the fallback service. The section renders the no-products
    // container whenever nothing matched.
    notifyEmptyResults(term) {
      if (!term || !this.predictiveSearchResults) return;
      const isEmpty = this.predictiveSearchResults.querySelector(
        ".quick-search-no-products-container"
      );
      if (!isEmpty) return;

      if (typeof dispatchCustomEvent === "function") {
        dispatchCustomEvent("search:no-results", { query: term });
      } else {
        document.dispatchEvent(
          new CustomEvent("search:no-results", { detail: { query: term } })
        );
      }
    }

    restoreInitial() {
      this.classList.remove("loading");
      this.setAttribute("data-show", "false");
      if (this.initialResultsHTML != null && this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = this.initialResultsHTML;
      }
    }

    deactivateMegaMenu() {
      document
        .querySelectorAll(".mega-menu__dropdown")
        .forEach((el) => el.setAttribute("inactive", "true"));
    }

    activateMegaMenu() {
      document
        .querySelectorAll(".mega-menu__dropdown")
        .forEach((el) => el.removeAttribute("inactive"));
    }

    open() {
      this.setAttribute("data-show", "true");
      if (this.drawer) this.drawer.classList.add("active");
      document.body.classList.add("hide-overflow", "open-cc");
      this.closeBtns.forEach((btn) => btn.classList.add("is-active"));
      this.deactivateMegaMenu();

      const input = this.inputs[0];
      if (input && document.activeElement !== input) {
        setTimeout(() => input.focus({ preventScroll: true }), 100);
      }

      if (typeof dispatchCustomEvent === "function") {
        dispatchCustomEvent("search:open");
      }
    }

    close() {
      this.setAttribute("data-show", "false");
      if (this.drawer) this.drawer.classList.remove("active");
      document.body.classList.remove("hide-overflow", "open-cc");
      this.closeBtns.forEach((btn) => btn.classList.remove("is-active"));
      this.activateMegaMenu();
    }

    toggle() {
      if (this.drawer && this.drawer.classList.contains("active")) {
        this.close();
      } else {
        this.open();
      }
    }
  }

  customElements.define("predictive-search-v2", PredictiveSearchV2);
}

